import { useReducedMotion } from 'motion/react';
import { memo, useCallback, useMemo, useState } from 'react';

import { useTrackAgentActionsMenuOpened } from '@air/analytics';
import { PaintbrushVerticalIcon } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { usePrivateWorkspaceAgentActionsMenuOpen } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/PrivateWorkspaceAgentActionsMenuOpenProvider';
import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import { PrivateWorkspaceAgentBoardTemplatesFlow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/PrivateWorkspaceAgentBoardTemplatesFlow';
import { PrivateWorkspaceAgentImprovedNullState } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/PrivateWorkspaceAgentImprovedNullState';
import { WorkspaceAgentPanelZeroStateSuggestionRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/WorkspaceAgentPanelZeroStateSuggestionRow';
import { PrivateWorkspaceAgentOnboardingAssetStack } from '~/components/v2/components/workspace/workspace-agent-panel/components/messages/PrivateWorkspaceAgentOnboardingAssetStack';
import { isWorkspaceAgentGuidedVariantSuggestionId } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentGuidedVariantEdits';
import { pickWorkspaceAgentImprovedNullStateChips } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentImprovedNullState';
import {
  getAttachedBatchIntro,
  getWorkspaceAgentHeroStructuredDisplay,
  getWorkspaceAgentPanelZeroStateIntro,
  getWorkspaceAgentPanelZeroStateSuggestionById,
  UPLOADED_BATCH_SUGGESTIONS,
  WORKSPACE_AGENT_PANEL_ACTIONS_MENU_CHIP,
  WORKSPACE_AGENT_PANEL_ZERO_STATE_CHIPS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelZeroStateSuggestions';
import { WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_DURATION_S } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentZeroStateEntrance';
import { usePrivateWorkspaceAgentGuidedVariantFlowTracking } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentGuidedVariantFlowTracking';
import { useWorkspaceAgentCreditsDepleted } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentCreditsDepleted';
import {
  usePrivateWorkspaceAgentPanelComposerPills,
  usePrivateWorkspaceAgentPanelSession,
} from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { useUploadedBatchOfferStore } from '~/components/v2/components/workspace/workspace-agent-panel/uploaded-batch-offer/useUploadedBatchOfferStore';
import { getWorkspaceAgentGuidedVariantsOpenedEntryForSuggestion } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantAnalytics';
import { useWorkspaceAgentComposerSeed } from '~/components/v2/components/workspace/workspace-agent-panel/WorkspaceAgentComposerSeedProvider';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export const PrivateWorkspaceAgentPanelZeroState = memo(() => {
  const { hasExplicitSelection } = usePrivateWorkspaceAgentPanelComposerPills();
  const { isLoadingSessions, sessionId, sessions } = usePrivateWorkspaceAgentPanelSession();
  const { blockIfCreditsDepleted, isCreditsDepleted } = useWorkspaceAgentCreditsDepleted();
  const showQuickReplyShimmer = !isLoadingSessions && sessions.length === 0;
  const { seedComposer } = useWorkspaceAgentComposerSeed();
  const { improvedAgentNullState } = useTemporaryFeatureFlags();
  const boardTemplatesFlowBoard = useWorkspaceAgentPanelStore((state) => state.boardTemplatesFlowBoard);
  const { openActionsMenu } = usePrivateWorkspaceAgentActionsMenuOpen();
  const isGuidedVariantFlowOpen = useWorkspaceAgentGuidedVariantFlowStore((state) => state.isGuidedVariantFlowOpen);
  const openGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.openGuidedVariantFlow);
  const { trackAgentActionsMenuOpened } = useTrackAgentActionsMenuOpened();
  const { trackGuidedVariantFlowOpened } = usePrivateWorkspaceAgentGuidedVariantFlowTracking({ chatId: sessionId });
  const attachedBatch = useUploadedBatchOfferStore((state) => state.attachedBatch);
  const attachments = useUploadedBatchOfferStore((state) => state.attachments);
  const uploadedBatchThumbnailUrls = useMemo(
    () => attachments.flatMap((attachment) => (attachment.thumbnailUrl ? [attachment.thumbnailUrl] : [])),
    [attachments],
  );
  const uploadedBatchPendingThumbnailCount = useMemo(
    () => attachments.filter((attachment) => attachment.isThumbnailPending).length,
    [attachments],
  );
  // `useState` (not `useRef`) so re-mounting the zero state re-runs the entrance animation.
  const [isVisible] = useState(true);
  const shouldReduceMotion = useReducedMotion();
  const transitionDuration = shouldReduceMotion ? 0 : WORKSPACE_AGENT_ZERO_STATE_ENTRANCE_DURATION_S;

  const handleSuggestionSelect = useCallback(
    (suggestionId: string) => {
      if (blockIfCreditsDepleted()) {
        return;
      }

      if (suggestionId === WORKSPACE_AGENT_PANEL_ACTIONS_MENU_CHIP.id) {
        trackAgentActionsMenuOpened({ location: 'zero_state_chip' });
        openActionsMenu('zero_state_chip');
        return;
      }

      if (isWorkspaceAgentGuidedVariantSuggestionId(suggestionId)) {
        trackGuidedVariantFlowOpened({
          entry: getWorkspaceAgentGuidedVariantsOpenedEntryForSuggestion(suggestionId),
        });
        openGuidedVariantFlow();
        return;
      }

      const suggestion = getWorkspaceAgentPanelZeroStateSuggestionById(suggestionId);

      if (!suggestion) {
        return;
      }

      // Sending up front when the user has explicit per-asset selection is the natural follow-through.
      // Board select-all is handled by the selection bar, not composer scope — keep seed-only here (WORK-373).
      seedComposer(suggestion.prompt, {
        sendImmediately: hasExplicitSelection,
        source: 'hero_chip',
        structuredDisplay: hasExplicitSelection ? getWorkspaceAgentHeroStructuredDisplay(suggestionId) : undefined,
      });
    },
    [
      blockIfCreditsDepleted,
      hasExplicitSelection,
      seedComposer,
      openActionsMenu,
      openGuidedVariantFlow,
      trackAgentActionsMenuOpened,
      trackGuidedVariantFlowOpened,
    ],
  );

  const suggestions = useMemo(() => {
    const chips = attachedBatch ? UPLOADED_BATCH_SUGGESTIONS : WORKSPACE_AGENT_PANEL_ZERO_STATE_CHIPS;

    return chips.map((chip) =>
      isWorkspaceAgentGuidedVariantSuggestionId(chip.id)
        ? { ...chip, icon: PaintbrushVerticalIcon, title: 'Make edits and variants' }
        : chip,
    );
  }, [attachedBatch]);

  const intro = attachedBatch
    ? getAttachedBatchIntro(attachedBatch)
    : getWorkspaceAgentPanelZeroStateIntro(hasExplicitSelection);

  const showsImprovedNullState = improvedAgentNullState && !attachedBatch && !boardTemplatesFlowBoard;

  if (isGuidedVariantFlowOpen && !boardTemplatesFlowBoard) {
    return null;
  }

  if (showsImprovedNullState) {
    return (
      <PrivateWorkspaceAgentImprovedNullState
        hasExplicitSelection={hasExplicitSelection}
        isVisible={isVisible}
        onSuggestionSelect={handleSuggestionSelect}
        showQuickReplyShimmer={showQuickReplyShimmer}
        suggestions={pickWorkspaceAgentImprovedNullStateChips(suggestions)}
        transitionDuration={transitionDuration}
      />
    );
  }

  return (
    <div className="mt-auto flex flex-col gap-4 px-1 pt-5 pb-3" data-testid="WORKSPACE_AGENT_PANEL_ZERO_STATE">
      {boardTemplatesFlowBoard ? (
        <PrivateWorkspaceAgentBoardTemplatesFlow board={boardTemplatesFlowBoard} />
      ) : (
        <>
          {attachedBatch ? (
            <PrivateWorkspaceAgentOnboardingAssetStack
              idPrefix={`uploaded-batch-${attachedBatch.batchId}`}
              pendingThumbnailCount={uploadedBatchPendingThumbnailCount}
              thumbnailUrls={uploadedBatchThumbnailUrls}
              totalCount={Math.max(attachedBatch.fileCount, attachments.length)}
            />
          ) : null}
          <p
            className={cn(
              'text-14 leading-5',
              isCreditsDepleted ? 'ads:text-secondary text-gray-11' : 'ads:text-primary text-gray-12',
            )}
            data-testid="WORKSPACE_AGENT_PANEL_ZERO_STATE_INTRO"
          >
            {intro}
          </p>
          <div className="flex flex-col items-start gap-2 overflow-visible py-0.5">
            {suggestions.map((suggestion, index) => (
              <WorkspaceAgentPanelZeroStateSuggestionRow
                index={index}
                isVisible={isVisible}
                key={suggestion.id}
                isDisabled={isCreditsDepleted}
                onSelect={handleSuggestionSelect}
                showQuickReplyShimmer={showQuickReplyShimmer}
                slotCount={suggestions.length}
                suggestion={suggestion}
                transitionDuration={transitionDuration}
                variant={improvedAgentNullState ? 'ghost' : 'outlined'}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
});

PrivateWorkspaceAgentPanelZeroState.displayName = 'PrivateWorkspaceAgentPanelZeroState';
